import React from 'react'
import Plot from 'react-plotly.js'

import { Layout } from 'plotly.js'

import { makeStyles, Theme, createStyles } from '@material-ui/core/styles'
import { Paper, Typography, IconButton, Tooltip } from '@material-ui/core'
import RefreshIcon from '@material-ui/icons/Refresh'
import CircularProgress from '@material-ui/core/CircularProgress'

import { PaymentTrends } from '../../../../hooks/useBilling'

interface PaymentTrendsChartProps {
    data: PaymentTrends
    isLoading: boolean
    onRefresh: () => void
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        paper: {
            padding: theme.spacing(2),
            marginBottom: theme.spacing(2),
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing(2),
        },
        refreshButton: {
            marginLeft: theme.spacing(1),
        },
    }),
)

export const PaymentTrendsChart: React.FC<PaymentTrendsChartProps> = ({
    data = {},
    isLoading,
    onRefresh,
}) => {
    const classes = useStyles()
    console.log(data)

    // Extract months and values from the new PaymentTrends object
    const monthEntries = Object.entries(data || {})
        .filter(
            ([month, value]) =>
                value &&
                typeof value.total_paid === 'number' &&
                typeof value.total_invoiced === 'number',
        )
        .sort(([a], [b]) => a.localeCompare(b)) // sort months ascending

    const months = monthEntries.map(([month]) => {
        const [year, monthNum] = month.split('-').map(Number)
        const date = new Date(year, monthNum - 1)
        return date.toLocaleDateString('en-US', {
            month: 'short',
            year: 'numeric',
        })
    })
    const payments = monthEntries.map(([, value]) => value.total_paid || 0)
    const invoices = monthEntries.map(([, value]) => value.total_invoiced || 0)

    const hasData = months.length > 0

    const collectionRates = monthEntries.map(([, value]) =>
        value.total_invoiced > 0
            ? (value.total_paid / value.total_invoiced) * 100
            : 0,
    )

    const plotData = [
        {
            x: months,
            y: invoices,
            name: 'Invoices',
            type: 'bar' as const,
            marker: { color: '#ff7f0e' },
        },
        {
            x: months,
            y: payments,
            name: 'Payments',
            type: 'bar' as const,
            marker: { color: '#1f77b4' },
        },
        {
            x: months,
            y: new Array(months.length).fill(null), // invisible
            mode: 'none',
            showlegend: false,
            hovertemplate:
                '<b>%{x}</b><br>' +
                'Invoices: $%{customdata[0]:,.2f}<br>' +
                'Payments: $%{customdata[1]:,.2f}<br>' +
                'Collection Rate: %{customdata[2]:.1f}%<extra></extra>',
            customdata: invoices.map((inv, i) => [
                inv,
                payments[i],
                collectionRates[i],
            ]),
        },
    ]

    // Calculate max for y-axis
    const maxValue = Math.max(...payments, ...invoices, 0)
    const yAxisMax = maxValue * 1.1

    const viewHeight = window.innerHeight - 350

    const layout: Partial<Layout> = {
        barmode: 'group',
        height: viewHeight,
        margin: { l: 50, r: 20, t: 20, b: 60 },
        xaxis: {
            title: { text: 'Month', standoff: 10 },
            tickangle: -45,
            automargin: true,
        },
        yaxis: {
            title: { text: 'Amount ($)', standoff: 10 },
            range: [0, yAxisMax],
            tickprefix: '$',
            automargin: true,
        },
        legend: {
            orientation: 'h',
            y: -0.2,
        },
        hovermode: 'x unified',
    }

    return (
        <Paper className={classes.paper}>
            <div className={classes.header}>
                <Typography variant="h6">Payment Trends</Typography>
                <Tooltip title="Refresh data">
                    <IconButton
                        onClick={onRefresh}
                        disabled={isLoading}
                        className={classes.refreshButton}
                    >
                        {isLoading ? (
                            <CircularProgress size={24} />
                        ) : (
                            <RefreshIcon />
                        )}
                    </IconButton>
                </Tooltip>
            </div>
            {hasData ? (
                <Plot
                    data={plotData}
                    layout={layout}
                    config={{ responsive: true }}
                    style={{ width: '100%' }}
                />
            ) : (
                <div
                    style={{
                        height: 300,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Typography color="textSecondary">
                        No payment trend data available
                    </Typography>
                </div>
            )}
        </Paper>
    )
}
